<template>
  <content-subpage
    class="media-settings-content-subpage"
    :back-label="backLabel"
    :content-title="t('media_settings_transcript_page_title')"
    :has-parent-subpage="hasParentSubpage"
    :titles="titles"
    :show-content-title="isViewingTranscript"
    :visible="visible"
    @content-subpage-back="handleBack"
    @content-subpage-hidden="handleCloseSubpage"
  >
    <template #title-buttons>
      <star-resource-button
        v-if="showStarButton"
        type="button"
        :resource-uuid="uuid"
        resource-type="MEDIA"
      />
    </template>
    <template #header-buttons>
      <fetch-data-button
        v-if="showSaveMediaSettings"
        class="save"
        :kind="dataSaveButton.kind"
        :size="dataSaveButton.size"
        :icon-only="dataSaveButton.iconOnly"
        :loading="dataSaveButton.loading || isProcessing"
        :completed="dataSaveButton.completed"
        :tip-position="dataSaveButton.tipPosition"
        :tip-alignment="dataSaveButton.tipAlignment"
        :error="dataSaveButton.error"
        :error-message="dataSaveButton.errorMessage"
        :disabled="(isGeneratingTranscript && isViewingTranscript) || isProcessing"
        @reset-error-state="handleSaveButtonResetError"
        @reset-completed-state="handleSaveButtonResetCompleted"
        @click="handleSave"
      >
        <svg-icon :name="dataSaveButton.iconName" kind="action" />
      </fetch-data-button>

      <fetch-data-button
        v-if="showEditIPDetails"
        class="edit"
        kind="primary"
        size="field"
        :icon-only="true"
        @click="handleEditIPDetails"
      >
        <svg-icon name="Pencil24" kind="action" />
      </fetch-data-button>

      <overflow-menu-button v-if="showHeaderOverflowMenuButton" kind="primary" size="field">
        <span>{{ t("media_object_actions_btn") }}</span>
        <template #overflow-menu>
          <OverflowMenuItem v-if="showEditBtn" @click="handleEdit">
            <svg-icon name="Pencil24" kind="action" />
            <span>{{ t("media_object_edit_btn") }}</span>
          </OverflowMenuItem>
          <OverflowMenuItem v-if="showSaveBtn" @click="handleSaveMO">
            <svg-icon name="Save24" kind="action" />
            <span>{{ t("media_object_save_btn") }}</span>
          </OverflowMenuItem>
          <OverflowMenuItem v-if="showSubmitForApprovalBtn" @click="handleSubmitForApproval">
            <svg-icon name="Proceed24" kind="action" />
            <span>{{ t("media_object_submit_for_approval_btn") }}</span>
          </OverflowMenuItem>
          <OverflowMenuItem v-if="showApproveBtn" @click="handleApprove">
            <svg-icon name="CheckmarkCircle24" kind="action" />
            <span>{{ t("media_object_approve_btn") }}</span>
          </OverflowMenuItem>
          <OverflowMenuItem v-if="showRejectBtn" @click="handleReject">
            <svg-icon name="CrossCircle24" kind="action" />
            <span>{{ t("media_object_reject_btn") }}</span>
          </OverflowMenuItem>
          <OverflowMenuItem v-if="showUnpublishBtn" @click="handleUnpublish">
            <svg-icon name="UnpublishMediaObject24" kind="action" />
            <span>{{ t("media_object_unpublish_btn") }}</span>
          </OverflowMenuItem>
          <OverflowMenuItem v-if="showDeleteBtn" @click="handleDelete">
            <svg-icon name="Trash24" kind="action" />
            <span>{{ t("media_object_delete_btn") }}</span>
          </OverflowMenuItem>
        </template>
      </overflow-menu-button>
    </template>

    <template #main-content>
      <template v-if="!isViewingIntellectualPropertyDetail">
        <MediaSettingEditForm
          v-if="editable"
          :id="id"
          :params="params"
          :media-object-metadata="mediaObjectMetadata"
          :character-limit="characterLimit"
          :error="error"
          :show-transcript-inline-notification="showTranscriptInlineNotification"
          :strict-mode="strictModeLocal"
          :transcription-file-version="transcriptionFileVersion"
          :caption-src="captionSrc"
          :cue-points="cuePoints"
          @input="handleTitleChange"
          @change="handleDescriptionChange"
          @media-loaded="handleMediaLoaded"
          @media-playable="handleMediaPlayable"
          @media-ready="handleMediaReady"
          @media-ref="handleMediaRef"
        />
        <MediaSettingViewForm
          v-else
          :params="params"
          :media-object-metadata="mediaObjectMetadata"
          :show-transcript-inline-notification="showTranscriptInlineNotification"
          :strict-mode="strictModeLocal"
          :transcription-file-version="transcriptionFileVersion"
          :is-viewing-transcript="isViewingTranscript"
          :caption-src="captionSrc"
          @media-loaded="handleMediaLoaded"
          @media-playable="handleMediaPlayable"
          @media-ready="handleMediaReady"
          @media-ref="handleMediaRef"
        />
      </template>
      <intellectual-property-detail
        v-else
        :edit-form-data="ipFormData"
        :error="error"
        :has-error-in-page="hasErrorInPage"
        :has-save-i-p-errors="hasSaveIPErrors"
        :uuid="uuid"
        :editable="editable"
        :subject-list="subjectList"
        :level-list="levelList"
        :owner-group="mediaObject?.ownerGroupString ?? ''"
        @ip-content-change="handleIpContentChange"
        @ip-error-status="handleIpErrorStatus"
      />
    </template>
    <template v-if="!isViewingIntellectualPropertyDetail" #sub-content>
      <overlay-loader v-if="isLoading" />
      <template v-else-if="!isViewingTranscript">
        <div class="form-group media-settings">
          <cv-form v-if="showMOStatus">
            <!-- Status -->
            <dl class="field-set type-text status">
              <dt class="field-label">{{ t("search_resource_detail_status_label") }}</dt>
              <dd class="field-value status-label">
                <label-component
                  kind="resource-status"
                  :text="displayStatus(mediaObject?.status)"
                  :class="statusTagClass(mediaObject?.status)"
                />
              </dd>
              <dd class="field-value status-history">
                <link-button @click="handleViewStatusHistory">
                  {{ t("search_resource_detail_status_history_label") }}
                </link-button>
              </dd>
            </dl>
          </cv-form>

          <cv-form v-if="showCuePoints || showDownloadAndPrintPermission">
            <dl v-if="showCuePoints" class="field-set input-cue">
              <h5 class="form-header">{{ mediaSettingsLabel }}</h5>
              <dt class="field-label">{{ t("media_settings_cuepoints_label") }}</dt>
              <dd class="field-input">
                <ul class="timestamps">
                  <li class="timestamp">
                    <div class="time-input">
                      <text-input
                        ref="startTime"
                        name="startTime"
                        :value="startTimeTextInput ?? ''"
                        :class="cssClasses"
                        @keyup="handleStartTime"
                      />
                      <text-input
                        ref="endTime"
                        name="endTime"
                        :value="endTimeTextInput ?? ''"
                        :class="cssClasses"
                        @keyup="handleEndTime"
                      />
                    </div>
                    <div v-if="hasCueStartAndEndError" class="field-error-group">
                      <message-component class="field-error error">
                        {{ error.cueStartAndEnd }}
                      </message-component>
                    </div>
                  </li>
                  <li class="action">
                    <cv-button
                      class="button start"
                      kind="tertiary"
                      size="small"
                      @click="captureStart"
                    >
                      {{ t("media_settings_cue_start") }}
                    </cv-button>
                    <cv-button class="button end" kind="tertiary" size="small" @click="captureEnd">
                      {{ t("media_settings_cue_end") }}
                    </cv-button>
                  </li>
                </ul>

                <input-checkbox
                  id="checked-non-strict-mode"
                  v-model:selected-values="nonStrictModeSelectedValues"
                  value="checked-non-strict-mode"
                  @change="handleNonStrictModeChange"
                >
                  {{ t("media_settings_non_strict_mode_settings") }}
                </input-checkbox>

                <div class="view-transcript">
                  <link-button
                    :class="{ disabled: disableTranscript }"
                    :disabled="disableTranscript"
                    @click="showViewEditTranscriptSubpage"
                  >
                    {{ transcriptSubpageText }}
                  </link-button>
                  <cv-tooltip
                    v-if="disableTranscript"
                    direction="bottom"
                    alignment="start"
                    :tip="t('media_settings_transcript_page_disabled_tooltip')"
                  >
                    <svg-icon name="Info24" kind="action" />
                  </cv-tooltip>
                </div>
              </dd>
            </dl>

            <!-- Permission -->
            <h5
              v-if="showDownloadAndPrintPermission || showTranscriptAndCaptionLabel"
              class="form-header"
            >
              {{ t("media_object_settings_label_permissions") }}
            </h5>

            <!-- Download Permission -->
            <dl
              v-if="showDownloadAndPrintPermission"
              class="field-set type-checkbox download-permission"
            >
              <dt class="field-label">{{ t("media_settings_label_download_permission") }}</dt>
              <dd v-if="isMOEResource" class="field-value">
                <span class="message">
                  {{ downloadableRightDescription }}
                </span>
              </dd>
              <dd v-else class="field-input">
                <input-checkbox
                  id="checked-downloadable"
                  v-model:selected-values="moDownloadableSelectedValues"
                  value="checked-downloadable"
                  :disabled="isDownloadableCheckboxDisabled"
                  @change="handleDownloadableChange"
                >
                  {{ t("media_settings_downloadable_settings") }}
                </input-checkbox>
              </dd>
            </dl>

            <!-- Print Permission -->
            <dl
              v-if="showDownloadAndPrintPermission"
              class="field-set type-checkbox print-permission"
            >
              <dt class="field-label">{{ t("media_settings_label_print_permission") }}</dt>
              <dd v-if="isMOEResource" class="field-value">
                <span class="message">
                  {{ printableRightDescription }}
                </span>
              </dd>
              <dd v-else class="field-input">
                <input-checkbox
                  id="checked-printable"
                  v-model:selected-values="moPrintableSelectedValues"
                  value="checked-printable"
                  :disabled="isPrintableCheckboxDisabled"
                  @change="handlePrintableChange"
                >
                  {{ t("media_settings_printable_settings") }}
                </input-checkbox>
              </dd>
            </dl>

            <!-- HTML5 Grading Permission -->
            <dl
              v-if="isHtml5Interactive"
              class="field-set type-checkbox html5-interactive-markable"
            >
              <dt class="field-label">{{ t("media_settings_label_grading_permission") }}</dt>
              <dd class="field-input">
                <input-checkbox
                  id="checked-markable"
                  v-model:selected-values="moMarkableSelectedValues"
                  value="checked-markable"
                  :disabled="true"
                >
                  {{ t("media_settings_markable_settings") }}
                </input-checkbox>
              </dd>
            </dl>

            <!-- Transcript Permission  -->
            <dl
              v-if="showTranscriptAndCaptionLabel && (isVideo || isAudio)"
              class="field-set type-checkbox transcript-permission"
            >
              <dt class="field-label">{{ t("media_settings_label_transcript_permission") }}</dt>
              <dd class="field-input">
                <input-checkbox
                  id="checked-transcript-enabled"
                  v-model:selected-values="showTranscriptSelectedValues"
                  value="checked-transcript-enabled"
                  :disabled="!hasGeneratedTranscript"
                  @change="handleTranscriptEnabledChange"
                >
                  {{ transcriptCheckboxText }}
                </input-checkbox>
              </dd>
            </dl>
          </cv-form>

          <cv-form class="meta-data">
            <!-- information -->
            <h5 class="form-header">{{ t("media_object_general_information_label") }}</h5>

            <!-- location -->
            <dl v-if="isMOEResource" class="field-set type-text location">
              <dt class="field-label">{{ t("search_resource_detail_location_label") }}</dt>
              <dd class="field-value">
                <span class="message">
                  {{ location }}
                </span>
              </dd>
            </dl>

            <!-- File Size -->
            <dl class="field-set type-text file-size">
              <dt class="field-label">{{ t("search_resource_detail_file_size_label") }}</dt>
              <dd class="field-value">
                <span class="message">{{ fileSize }}</span>
              </dd>
            </dl>
            <!-- created by -->
            <dl v-if="owner" class="field-set type-text created-by">
              <dt class="field-label">{{ t("search_resource_detail_created_by_label") }}</dt>
              <dd class="field-value">
                <label-component
                  kind="creditor"
                  :class="{ 'creditor-moe': !canOpenProfile }"
                  :text="owner"
                  @click="handleProfileClick({ userId: createdById, isDisabled: isMOEResource })"
                />
                <time class="date">{{ mediaObjectCreatedDateUI }}</time>
              </dd>
            </dl>
            <!-- Modified by -->
            <dl class="field-set type-text modified-by">
              <dt class="field-label">{{ t("search_resource_detail_edited_label") }}</dt>
              <dd class="field-value">
                <label-component
                  kind="creditor"
                  :text="modifiedBy"
                  @click="handleProfileClick({ userId: modifiedById, isDisabled: false })"
                />
                <time class="date">{{ mediaObjectModifiedDateUI }}</time>
              </dd>
            </dl>
            <!-- keywords -->
            <dl class="field-set type-text keywords">
              <dt class="field-label">{{ t("media_settings_keywords") }}</dt>
              <dd v-if="editable" class="field-input">
                <keywords
                  ref="keywords"
                  :tags="mediaObjectMetadata.keywords"
                  :placeholder="keywordsPlaceholder"
                  :character-limit="characterLimit.keywords"
                  :is-input-disabled="isInputDisabled"
                  :is-tags-disabled="isTagsDisabled"
                  :delimiter="','"
                  mode="EDIT"
                  :message="task"
                  :text-id="id"
                  @keydown.enter.prevent
                  @input="handleKeywordsInput"
                  @remove="handleKeywordsRemove"
                  @executed="handleExecution"
                />
              </dd>
              <dd v-else class="field-value">
                <keywords v-if="showKeywords" :tags="keywordsTags" />
              </dd>
            </dl>
          </cv-form>

          <!-- Copyright Period and Access-->
          <cv-form v-if="showCopyrightPeriodAndAccess" class="copyright-period-access">
            <h5 class="form-header">
              {{ t("search_resource_detail_copyright_period_and_access_label") }}
            </h5>
            <!-- Licence Type -->
            <dl class="field-set type-text">
              <dt class="field-label">{{ t("media_object_field_label_licence_type") }}</dt>
              <dd class="field-value">
                {{ getLicenseTypeLabel }}
              </dd>
            </dl>

            <!-- start date -->
            <dl class="field-set type-text modified-by">
              <dt class="field-label">{{ t("search_resource_detail_start_date_label") }}</dt>
              <dd class="field-value">
                {{ startDate }}
              </dd>
            </dl>
            <!-- end date -->
            <dl class="field-set type-text modified-by">
              <dt class="field-label">{{ t("search_resource_detail_end_date_label") }}</dt>
              <dd class="field-value">
                {{ validity }}
              </dd>
            </dl>

            <!-- End User Access -->
            <dl class="field-set type-text availability">
              <dt class="field-label">{{ t("search_resource_detail_availability_label") }}</dt>
              <dd class="field-value">
                <span class="message">{{ availability }}</span>
              </dd>
            </dl>

            <!-- Subject -->
            <dl v-if="isRestricted" class="field-set type-text subject">
              <dt class="field-label">{{ t("search_filter_label_subject") }}</dt>
              <dd class="field-value">
                <span class="message">{{ getSubjectList }}</span>
              </dd>
            </dl>

            <!-- Level -->
            <dl v-if="isRestricted" class="field-set type-text level">
              <dt class="field-label">{{ t("search_filter_label_level") }}</dt>
              <dd class="field-value">
                <span class="message">{{ getLevelList }}</span>
              </dd>
            </dl>
          </cv-form>

          <!-- Used In -->
          <cv-form v-if="showLinkedResource" class="linked-resources">
            <dl class="field-set type-text used-in">
              <dt class="field-label">{{ t("search_resource_detail_used_in_label") }}</dt>
              <dd class="field-value">
                <linked-resources-table-list :resource-uuid="uuid" />
              </dd>
            </dl>
          </cv-form>
        </div>

        <div class="sub-content-action">
          <cv-inline-notification
            v-if="hasErrorInPage"
            class="account-audit-error"
            :title="t('media_object_check_for_error_message')"
            low-contrast
            kind="error"
            hide-close-button
          />
          <div v-if="showIPDetailsBtn" class="button-wrapper">
            <cv-button
              class="btn-intellectual-property"
              type="button"
              kind="tertiary"
              size="field"
              @click="handleShowIntellectualPropertyDetail"
            >
              <span>{{ t("media_object_intellectual_property_details") }}</span>
            </cv-button>
          </div>
        </div>
      </template>

      <div v-else-if="isViewingTranscript" class="transcript-settings">
        <h5 class="transcript-title">{{ t("media_settings_transcript_label") }}</h5>
        <cv-inline-notification
          v-if="showTranscriptError"
          class="transcript-error"
          kind="error"
          :title="t('media_settings_transcript_error_title')"
          low-contrast
          hide-close-button
        >
          <template #subtitle>
            <div v-safe-html="transcriptionErrorSubtitle" />
          </template>
        </cv-inline-notification>
        <div class="transcript-content">
          <media-transcription
            v-if="hasTranscript && !isGeneratingTranscript"
            ref="mediatrack"
            :caption-src="captionSrc"
            :is-video="isVideo"
            :can-edit-transcript="canEditTranscript"
            @transcript-edited="handleTranscriptEdited"
            @track-loaded="handleTrackLoaded"
          />
          <empty-placeholder v-if="isGeneratingTranscript || !hasTranscript">
            <template #icon><GenericEmpty /></template>
            <span>{{ transcriptText }}</span>
          </empty-placeholder>
        </div>

        <div v-if="canEditTranscript" class="action">
          <fetch-data-button
            class="submit"
            v-bind="generateTranscriptButton"
            :loading="isGeneratingTranscript"
            :disabled="!isMediaReady"
            @click="handleGenerateTranscript"
          >
            <span>{{ generateTranscriptButtonText }}</span>
          </fetch-data-button>
        </div>
      </div>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, provide, ref, useTemplateRef, watch } from "vue";
import { getValueOrDefault, isDefined, isEmpty, isNull, notNull } from "@utils/var.check";
import GenericEmpty from "@images/placeholder/empty-states/GenericEmpty.svg?component";
import Keywords from "@plugins/keywords/components/Keywords.vue";
import MediaTranscription from "@plugins/media/components/MediaTranscription.vue";
import MediaSettingEditForm from "@plugins/media/components/MediaSettingsEditForm.vue";
import MediaSettingViewForm from "@plugins/media/components/MediaSettingsViewForm.vue";
import LinkedResourcesTableList from "@modules/search/components/LinkedResourcesTableList.vue";
import IntellectualPropertyDetail from "@modules/manage-resource/components/IntellectualPropertyDetail.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";
import LabelComponent from "@plugins/common/components/LabelComponent.vue";
import InputCheckbox from "@plugins/common/components/InputCheckbox.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import LinkButton from "@plugins/common/components/LinkButton.vue";
import MessageComponent from "@plugins/common/components/MessageComponent.vue";
import TextInput from "@plugins/common/components/TextInput.vue";
import OverflowMenuItem from "@plugins/common/components/OverflowMenuItem.vue";
import OverflowMenuButton from "@plugins/common/components/OverflowMenuButton.vue";
import StarResourceButton from "@plugins/common/components/StarResourceButton.vue";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { useProcessingState } from "@modules/app/composables/processing-state.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useTextInputValidation } from "@plugins/common/composables/use-text-input-validation.ts";
import { useMediaObject } from "@plugins/media/composables/use-media-object.ts";
import { useSearchProfile } from "@/modules/search/composables/use-search-profile.ts";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { useRoute, useRouter } from "vue-router";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";
import FileUploaderHidden from "@plugins/file-uploader-v2/components/FileUploaderHidden.vue";
import StatusHistoryContentSubpage from "@plugins/status-history/components/StatusHistoryContentSubpage.vue";
import ManageResourceStore from "@modules/manage-resource/components/ManageResourceStore";
import { NAMESPACE } from "@modules/editor/service/editor.constant";
import { STATUS } from "@modules/manage-resource/service/manage.resource.constant";
import {
  RESOURCE_DOMAIN,
  RESOURCE_DOMAIN_PATH,
  RESOURCE_TYPE,
} from "@plugins/common/constant/resource.constant";
import {
  AVAILABILITY,
  DOWNLOADABLE_RIGHT,
  LICENSE_TYPE,
  MEDIA_TYPE,
  PRINTABLE_RIGHT,
  RMS_TYPE,
  TRANSCRIPTION_STATUS,
} from "@plugins/media/media.constant";
import { LOCKABLE_RESOURCE_TYPE } from "@plugins/common/service/lock.constant";
import {
  ExtendedMediaObjectEditModel,
  MediaObjectCopyRightModel,
  MediaObjectFormData,
  MediaObjectManageModel,
  MediaSettingsParams,
  TranscriptionJobModel,
} from "@plugins/media/types.ts";
import { UploadFlow } from "@plugins/file-uploader-v2/types.ts";
import { LevelAndStream } from "@modules/level-and-stream/types";
import { Subject } from "@modules/student-group/student-group-types";
import MediaUtils from "@plugins/media/media.utils";
import RandomUtils from "@utils/random.utils";
import RichTextUtils from "@plugins/richtext/utils/richtext.utils";
import DateTimeUtils from "@utils/datetime.utils";
import DOMUtils from "@utils/dom.utils";
import PollingUtils from "@utils/polling.utils";
import SecurityUtils from "@utils/security.utils";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import DateTimeFormatter from "@utils/datetime.formatter";
import MediaObjectApi from "@apis/media.object.api";
import OwnerGroupApi from "@apis/owner.group.api";
import LevelStreamApi from "@apis/level.stream.api";
import SubjectApi from "@apis/subject.api.ts";
import LockApi from "@apis/lock.api";
import MediaEventBus from "@plugins/media/service/event-bus";
import appConfigs from "@configs/app";
import { ApiResponse } from "@/apis/api";

// Props
const props = defineProps<{ params: MediaSettingsParams }>();

// Emits
const emit = defineEmits<{
  (e: "failed", payload: { error: unknown }): void;
  (e: "updateMediaObject", mediaObject: ExtendedMediaObjectEditModel): void;
}>();

// Composables
const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();
const modal = useModal();
const toast = useToast();
const contentSubpageInstance = useContentSubpageInstance();
const { handleProfileClick } = useSearchProfile();
const { passiveApiFailHandler } = useErrorHandler();
const { validateMaxCharacterLimit } = useTextInputValidation();

// Data
const id = ref(RandomUtils.randomId());
const isLoading = ref(true);
const mediaObject = ref<ExtendedMediaObjectEditModel | null>(null);
const uuid = ref(props.params.uuid);
const mediaType = ref(props.params.mediaType);
const cueStart = ref<string | null>(MediaUtils.formatTime(props.params.cuePoints?.start));
const cueEnd = ref<string | null>(MediaUtils.formatTime(props.params.cuePoints?.end));
const lastCueStart = ref<string | null>(MediaUtils.formatTime(props.params.cuePoints?.start));
const lastCueEnd = ref<string | null>(MediaUtils.formatTime(props.params.cuePoints?.end));
const moResourceDomain = ref<string | null>(getValueOrDefault(props.params.resourceDomain, null));
const error = ref<Record<string, string>>({
  cueStartAndEnd: "",
  title: "",
  description: "",
  keyword: "",
  owner: "",
  copyRightAuthor: "",
  authorAcknowledgement: "",
  author: "",
  authorAttribution: "",
  rmsType: "",
  licenseType: "",
  startDate: "",
  endDate: "",
  territory: "",
  reproduction: "",
  downloadableRight: "",
  printableRight: "",
  modificationRight: "",
  modificationType: "",
  modificationDetails: "",
  licensorName: "",
  licensorContact: "",
  licenseSource: "",
  licensorRequest: "",
  availability: "",
  levelAndStreams: "",
  subjects: "",
});
const mediaObjectMetadata = ref({
  title: "",
  description: "",
  keywords: "",
  downloadableRight: "",
  printableRight: "",
});
const richTextDescription = ref("");
const dirty = ref(false);
const isProcessing = ref(false);
const isSettingsDirty = ref(false);
const duration = ref(0);
const task = ref("");
const execution = ref<Record<string, unknown>>({});
const characterLimit = {
  title: 500,
  description: 1000,
  keywords: 500,
};
const strictModeLocal = ref<boolean>(props.params.strictMode ?? false);
const lastStrictModeLocal = ref<boolean>(props.params.strictMode ?? false);
const lastValidEndTime = ref<string | null>(null);
const lastValidStartTime = ref<string | null>(null);
const endTimeTextInput = ref<string | null>(null);
const startTimeTextInput = ref<string | null>(null);
const licenseDuration = ref<Record<string, number>>({});
const isViewingTranscript = ref(false);
const isGeneratingTranscript = ref(false);
const generateTranscriptButton = ref({
  kind: "tertiary",
  completed: false,
  loading: false,
  error: false,
});
const transcriptionFileVersion = ref<string | undefined>(props.params.transcriptionFileVersion);
const isTranscriptEnabledLocal = ref<boolean>(props.params.isTranscriptEnabled ?? false);
const lastTranscriptEnabledLocal = ref<boolean>(props.params.isTranscriptEnabled ?? false);
const trackCues = ref<TextTrackCueList | null>(null);
const isTranscriptEdited = ref(false);
const showTranscriptError = ref(false);
const captionSrc = ref(props.params.captionSrc ?? "");
const hasGeneratedTranscript = ref(false);
const canEditTranscript = ref(false);
const hasParentSubpage = ref(props.params.hasParentSubpage ?? false);
const isMediaReady = ref(false);
const moDownloadableSelectedValues = ref<string[]>([]);
const moPrintableSelectedValues = ref<string[]>([]);
const moMarkableSelectedValues = ref<string[]>([]);
const showTranscriptSelectedValues = ref<string[]>([]);
const nonStrictModeSelectedValues = ref<string[]>([]);
const manageResourceStoreRef = ref(ManageResourceStore);
const isViewingIntellectualPropertyDetail = ref(false);
const parentSubpageName = ref(props.params.parentSubpageName ?? "");
const mediaObjectIPData = ref<MediaObjectCopyRightModel | null>(null);
const ipFormData = ref<MediaObjectFormData>(MediaUtils.createBlankIpFormData());
const hasErrorInPage = ref(false);
const hasSaveIPErrors = ref(false);
const userOwnerGroups = ref<{ id: number }[]>([]);
const isEditClick = ref(false);
const levelList = ref<LevelAndStream[]>([]);
const subjectList = ref<Subject[]>([]);
const mediaRef = ref<InstanceType<typeof MediaComponent> | null>(null);
const mediatrackRef = useTemplateRef<InstanceType<typeof MediaTranscription>>("mediatrack");
const startTimeRef = useTemplateRef<InstanceType<typeof TextInput>>("startTime");
const endTimeRef = useTemplateRef<InstanceType<typeof TextInput>>("endTime");

const dirtyId = computed(() => (isViewingTranscript.value ? `${id.value}_transcript` : id.value));
const processingId = computed(() =>
  isViewingTranscript.value ? `${id.value}_transcript` : id.value,
);

const { setProcessing, resetProcessing } = useProcessingState(processingId.value);
const {
  useMediaObject_validateCharacterLimit,
  useMediaObject_validateEmptyField,
  useMediaObject_validateEndDate,
  useMediaObject_saveMediaObjectIPDetails,
  useMediaObject_showSubmitForApprovalModal,
  useMediaObject_submitForApproval,
  useMediaObject_showApproveModal,
  useMediaObject_approve,
  useMediaObject_showRejectModal,
  useMediaObject_reject,
  useMediaObject_showUnpublishModal,
  useMediaObject_unpublish,
  useMediaObject_getExpirationDate,
} = useMediaObject(ipFormData.value, error.value);

const {
  defaultExpose,
  titles,
  dataSaveButton,
  visible,
  handleSaveButtonResetError,
  handleSaveButtonResetCompleted,
  isDirty,
  setDirty,
  removeDirty,
  actionHidden,
  actionBack,
} = useContentSubpage(props.params, dirtyId);

defineExpose(defaultExpose);

// Computed
const isCO = computed(() => store.getters[`${NAMESPACE.AUTH}/isCO`]);
const isCA = computed(() => store.getters[`${NAMESPACE.AUTH}/isCA`]);
const resourceDomain = computed(() => store.getters[`${NAMESPACE.LESSON}/resourceDomain`]);

const backLabel = computed(() => {
  if (isVideo.value) {
    return t("media_settings_transcript_back_video_label");
  }
  if (isAudio.value) {
    return t("media_settings_transcript_back_audio_label");
  }
  return `${t("content_subpage_go_back_link")} ${parentSubpageName.value}`;
});

const hasTranscript = computed(() => !isEmpty(captionSrc.value));

const transcriptText = computed(() => {
  if (!hasTranscript.value && !isGeneratingTranscript.value) {
    return t("media_settings_transcript_empty_text");
  }
  return t("media_settings_transcript_in_progress_text");
});

const generateTranscriptButtonText = computed(() => {
  if (!hasTranscript.value) {
    return t("media_settings_generate_transcript_button");
  }
  return t("media_settings_regenerate_transcript_button");
});

const transcriptSubpageText = computed(() => {
  if (canEditTranscript.value) {
    return t("media_settings_transcript_view_edit_link_button_text");
  }
  return t("media_settings_transcript_view_link_button_text");
});

const showSaveMediaSettings = computed(() => {
  if (isViewingIntellectualPropertyDetail.value && editable.value) {
    return isCAOrCO.value;
  }
  if (!showCuePoints.value && isMOEResource.value) {
    return false;
  }
  if (isViewingTranscript.value && isFromLesson.value) {
    return canEditTranscript.value;
  }
  return (
    (editable.value || playable.value) &&
    !isViewingIntellectualPropertyDetail.value &&
    !showHeaderOverflowMenuButton.value
  );
});

const showEditIPDetails = computed(
  () =>
    isViewingIntellectualPropertyDetail.value &&
    !editable.value &&
    isSameOwnerGroup.value &&
    showEditBtn.value,
);

const showIPDetailsBtn = computed(() => {
  if (!isCAOrCO.value) return false;

  // lesson editor - only show for MOE lesson & MOE media object
  if (isFromLesson.value) return isMOEModule.value && isMOEResource.value;

  return isMOEResource.value;
});

const showHeaderOverflowMenuButton = computed(() => {
  const isViewingDetails = isViewingTranscript.value || isViewingIntellectualPropertyDetail.value;
  const isPendingOrApproved =
    moStatus.value === STATUS.PENDING_APPROVAL.value || moStatus.value === STATUS.APPROVED.value;
  const isRestrictedByStatus = isCO.value && !isCA.value && isPendingOrApproved;

  if (isFromLesson.value) {
    return (
      isMOEModule.value && !isViewingDetails && !isRestrictedByStatus && isSameOwnerGroup.value
    );
  }

  if (!isSameOwnerGroup.value) return false;

  return (
    isMOEResource.value &&
    ((!isViewingDetails && !isRestrictedByStatus) || (!isViewingDetails && isCA.value))
  );
});

const disableTranscript = computed(() => !canEditTranscript.value && !hasTranscript.value);

const showTranscriptAndCaptionLabel = computed(
  () =>
    isFromLesson.value &&
    (canEditTranscript.value || hasTranscript.value) &&
    (isVideo.value || isAudio.value),
);

const playable = computed(
  () => mediaType.value === MEDIA_TYPE.VIDEO || mediaType.value === MEDIA_TYPE.AUDIO,
);

const isDownloadableCheckboxDisabled = computed(() => {
  return mediaObject.value?.readonly || mediaObject.value?.isDefaultDownloadable || !editable.value;
});

const isPrintableCheckboxDisabled = computed(() => {
  return mediaObject.value?.readonly || !editable.value;
});

const keywordsTags = computed(() => {
  if (!mediaObjectMetadata.value || !mediaObjectMetadata.value.keywords) return "";
  return mediaObjectMetadata.value.keywords;
});

const keywordsPlaceholder = computed(() => t("keywords_placeholder_label"));

const editable = computed(() => {
  // PERSONAL & ASSIGNMENT MO, only creator will be allowed to edit
  // MOE vle side cannot edit
  // CG/ALP_TEMPLATE only AlpAdmin can edit
  return !isEmpty(mediaObject.value) && !mediaObject.value!.readonly;
});

const showCuePoints = computed(() => isFromLesson.value && playable.value && !isTextToSpeech.value);

const showDownloadAndPrintPermission = computed(() => !isTextToSpeech.value);

const showKeywords = computed(
  () => mediaObjectMetadata.value.keywords && mediaObjectMetadata.value.keywords.length > 0,
);

const showTranscriptInlineNotification = computed(
  () =>
    canEditTranscript.value && isVideo.value && isViewingTranscript.value && hasTranscript.value,
);

const isInputDisabled = computed(() => isProcessing.value);

const isTagsDisabled = computed(() => isProcessing.value);

const isTextToSpeech = computed(() => {
  if (mediaObject.value && !isNull(mediaObject.value.description)) {
    return (mediaObject.value.description as string).includes("sls-tts");
  }
  return false;
});

const isMOEResource = computed(() => moResourceDomain.value === RESOURCE_DOMAIN.MOE);

const isCommunityResource = computed(() => moResourceDomain.value === RESOURCE_DOMAIN.COMMUNITY);

const isVideo = computed(() => mediaType.value === MEDIA_TYPE.VIDEO);

const isAudio = computed(() => mediaType.value === MEDIA_TYPE.AUDIO);

const isHtml5Interactive = computed(
  () => mediaType.value === MEDIA_TYPE.HTML5 && mediaObject.value?.markable,
);

const transcriptCheckboxText = computed(() => {
  if (isVideo.value) return t("media_settings_enable_transcript_checkbox_video_text");
  if (isAudio.value) return t("media_settings_enable_transcript_checkbox_audio_text");
  return null;
});

const location = computed(() => (isMOEResource.value ? t("moe_library_label") : "-"));

const owner = computed(() => {
  if (isMOEResource.value) {
    return t("media_settings_owner_moe") + " " + mediaObject.value?.ownerGroupString;
  }
  if (isCommunityResource.value) return t("media_settings_owner_community");
  return mediaObject.value?.createdByName;
});

const modifiedBy = computed(() => {
  if (isMOEResource.value && !isCAOrCO.value) {
    return t("media_settings_owner_moe");
  }
  if (isCommunityResource.value) return t("media_settings_owner_community");
  return mediaObject.value?.modifiedByName;
});

const cuePoints = computed(() => {
  if (isViewingTranscript.value) {
    return { start: 0, end: 0 };
  }
  const cueEndVal = MediaUtils.deformatTime(endTime.value);
  const cueStartVal = MediaUtils.deformatTime(startTime.value);
  if (cueEndVal === parseInt(String(duration.value)) && cueStartVal === 0) {
    return { start: 0, end: 0 };
  }
  return { start: cueStartVal, end: cueEndVal };
});

const startTime = computed(() => {
  if (!cueStart.value) return "00:00";
  return cueStart.value;
});

const endTime = computed(() => {
  // use video length as end time if no cue end point is provided
  // ensure duration is ready before checking with cue end point
  if (duration.value) return cueEnd.value;
  return MediaUtils.formatTime(duration.value);
});

const mediaObjectTitle = computed(() =>
  !isEmpty(mediaObjectMetadata.value) && !isEmpty(mediaObjectMetadata.value.title)
    ? (mediaObjectMetadata.value.title as string)
    : "",
);

const mediaObjectDescription = computed(() =>
  !isEmpty(mediaObjectMetadata.value) && !isEmpty(mediaObjectMetadata.value.description)
    ? (mediaObjectMetadata.value.description as string)
    : "",
);

const copyrightData = computed(() => mediaObject.value?.mediaObjectCopyRightModel);

const mediaObjectModifiedDateUI = computed(() =>
  DateTimeFormatter.formatToDateTimeAmPm(
    DateTimeUtils.parseLocalDateTime(mediaObject.value?.modifiedDate),
  ),
);

const mediaObjectCreatedDateUI = computed(() =>
  DateTimeFormatter.formatToDateTimeAmPm(
    DateTimeUtils.parseLocalDateTime(mediaObject.value?.createdDate),
  ),
);

const licenseType = computed(() => copyrightData.value?.licenseType ?? "");

const getLicenseTypeLabel = computed(() => {
  for (const key in LICENSE_TYPE) {
    if (LICENSE_TYPE[key].value === licenseType.value) {
      return t(LICENSE_TYPE[key].label);
    }
  }
  return "-";
});

const validity = computed(() =>
  useMediaObject_getExpirationDate(
    (copyrightData.value?.endDate as string) ?? null,
    licenseType.value,
  ),
);

const startDate = computed(() =>
  copyrightData.value && copyrightData.value.startDate
    ? DateTimeFormatter.formatToDateOnly(
        DateTimeUtils.parseLocalDateTime(copyrightData.value.startDate),
      )
    : "-",
);

const isRestricted = computed(() => copyrightData.value?.availability === "RESTRICTED");

const availability = computed(() => {
  const avail = copyrightData.value?.availability;
  if (avail && notNull(AVAILABILITY[avail])) {
    return t(AVAILABILITY[avail].label);
  }
  return "-";
});

const fileSize = computed(() => mediaObject.value?.fileSizeFriendly ?? "-");

const showStarButton = computed(() => {
  if (props.params.isExternal || !props.params.isExists || props.params.isDistributedSlideMedia) {
    if (moStatus.value === STATUS.APPROVED.value || (isCAOrCO.value && isSameOwnerGroup.value)) {
      return true;
    }
    return false;
  }
  return props.params.isMO;
});

const mediaSettingsLabel = computed(() => {
  if (mediaType.value === MEDIA_TYPE.VIDEO) return t("media_settings_video_settings");
  return t("media_settings_audio_settings");
});

const hasCueStartAndEndError = computed(() => !isEmpty(error.value.cueStartAndEnd));

const cssClasses = computed(() => ({ error: hasCueStartAndEndError.value }));

const transcriptionErrorSubtitle = computed(() =>
  t("media_settings_transcript_error_subtitle", {
    userGuideUrl: appConfigs.insertTranscriptUserGuideUrl,
  }),
);

const displayStatus = computed(() => {
  return (status: string | undefined) => {
    return status?.replaceAll("_", " ").toUpperCase();
  };
});

const statusTagClass = computed(() => {
  return (status: string | undefined) =>
    status?.trim().replace(" ", "-").replace("_", "-").toLowerCase();
});

const isCAOrCO = computed(() => isCA.value || isCO.value);

const isSameOwnerGroup = computed(() =>
  userOwnerGroups.value.some((ownerGroup) => ownerGroup.id === mediaObject.value?.ownerGroupId),
);

const showMOStatus = computed(() => {
  if (!isCAOrCO.value) return false;

  // lesson editor - only show for MOE lesson & MOE media object
  if (isFromLesson.value) return isMOEModule.value && isMOEResource.value;

  return isMOEResource.value;
});

const showCopyrightPeriodAndAccess = computed(
  () => isCAOrCO.value && isMOEResource.value && !editable.value,
);

const showLinkedResource = computed(() => isFromMedia.value && !editable.value);

const isFromMedia = computed(() => props.params.resourceType === RESOURCE_TYPE.MEDIA);

const isFromLesson = computed(() => props.params.resourceType === RESOURCE_TYPE.LESSON);

const isMOEModule = computed(() => resourceDomain.value === RESOURCE_DOMAIN_PATH.MOE);

const moStatus = computed(() =>
  mediaObject.value?.status?.trim().replaceAll(" ", "_").toUpperCase(),
);

const showEditBtn = computed(
  () =>
    !editable.value &&
    ((isCA.value && moStatus.value !== STATUS.APPROVED.value) ||
      (isCO.value &&
        moStatus.value !== STATUS.PENDING_APPROVAL.value &&
        moStatus.value !== STATUS.APPROVED.value)),
);

const showSaveBtn = computed(() => {
  // if is CA role and if mo is approve audio/mp3, show the button for CA to save for video/audio setting
  if (showCuePoints.value && isCA.value && moStatus.value === STATUS.APPROVED.value) {
    return true;
  }
  return (
    editable.value &&
    ((isCA.value && moStatus.value !== STATUS.APPROVED.value) ||
      (isCO.value &&
        moStatus.value !== STATUS.PENDING_APPROVAL.value &&
        moStatus.value !== STATUS.APPROVED.value))
  );
});

const showSubmitForApprovalBtn = computed(
  () =>
    !isCA.value &&
    isCO.value &&
    (moStatus.value === STATUS.DRAFT.value || moStatus.value === STATUS.REJECTED.value),
);

const showApproveBtn = computed(
  () =>
    isCA.value &&
    moStatus.value !== STATUS.APPROVED.value &&
    moStatus.value !== STATUS.UNPUBLISHED.value,
);

const showRejectBtn = computed(
  () => isCA.value && moStatus.value === STATUS.PENDING_APPROVAL.value,
);

const showDeleteBtn = computed(
  () =>
    (isCA.value &&
      moStatus.value !== STATUS.PENDING_APPROVAL.value &&
      moStatus.value !== STATUS.APPROVED.value) ||
    (isCO.value &&
      moStatus.value !== STATUS.PENDING_APPROVAL.value &&
      moStatus.value !== STATUS.APPROVED.value),
);

const showUnpublishBtn = computed(() => isCA.value && moStatus.value === STATUS.APPROVED.value);

const getSubjectList = computed(() => {
  const subjects = ipFormData.value.subjects;
  if (!subjects || subjects.length === 0) return "-";
  return subjects.map((item) => item.subjectName).join(", ");
});

const getLevelList = computed(() => {
  const levels = ipFormData.value.levelAndStreams;
  if (!levels || levels.length === 0) return "-";
  return levels.map((item) => item.displayName).join(", ");
});

const createdById = computed(() => mediaObject.value?.createdById as number);

const canOpenProfile = computed(() => !isMOEResource.value || createdById.value !== 0);

const modifiedById = computed(() => mediaObject.value?.modifiedById as number);

const downloadableRightDescription = computed(() => {
  const downloadableRight = mediaObjectMetadata.value.downloadableRight;
  if (downloadableRight in DOWNLOADABLE_RIGHT) {
    return t(DOWNLOADABLE_RIGHT[downloadableRight as keyof typeof DOWNLOADABLE_RIGHT].description);
  }
  return "";
});

const printableRightDescription = computed(() => {
  const printableRight = mediaObjectMetadata.value.printableRight;
  if (printableRight in PRINTABLE_RIGHT) {
    return t(PRINTABLE_RIGHT[printableRight as keyof typeof PRINTABLE_RIGHT].description);
  }
  return "";
});

// Methods
const handleBack = () => {
  promptPageDirty().then((response) => {
    if (response) {
      if (!isViewingTranscript.value) {
        EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
      }
      if (isViewingIntellectualPropertyDetail.value) {
        handleBackFromIntellectualPropertyDetail();
      } else if (isViewingTranscript.value) {
        handleBackFromTranscript();
      } else {
        actionBack();
      }
    }
  });
};

const handleTitleChange = (value: string) => {
  error.value.title = "";
  setDirty(id.value);
  isSettingsDirty.value = true;
  mediaObjectMetadata.value.title = value;
};

const handleDescriptionChange = (value: string) => {
  error.value.description = "";
  setDirty(id.value);
  isSettingsDirty.value = true;
  mediaObjectMetadata.value.description = value;
  setRichTextValue(value);
};

const handleNonStrictModeChange = () => {
  setDirty(id.value);
  strictModeLocal.value = !nonStrictModeSelectedValues.value.includes("checked-non-strict-mode");
};

const handleTranscriptEnabledChange = (checked: boolean | string[], isInit = false) => {
  if (!isInit) {
    setDirty(id.value);
  }
  isTranscriptEnabledLocal.value = Array.isArray(checked)
    ? checked.includes("checked-transcript-enabled")
    : checked;
};

const handleDownloadableChange = (checked: string[]) => {
  setDirty(id.value);
  isSettingsDirty.value = true;

  mediaObjectMetadata.value.downloadableRight = checked.includes("checked-downloadable")
    ? DOWNLOADABLE_RIGHT.YES.value
    : DOWNLOADABLE_RIGHT.NO.value;
};

const handlePrintableChange = (checked: string[]) => {
  setDirty(id.value);
  isSettingsDirty.value = true;
  mediaObjectMetadata.value.printableRight = checked.includes("checked-printable")
    ? PRINTABLE_RIGHT.YES.value
    : PRINTABLE_RIGHT.NO.value;
};

const validateInput = (): boolean => {
  validateEmpty();
  validateCharacterLimitLocal();

  let isValid = true;
  for (const key in error.value) {
    if (!isEmpty(error.value[key])) {
      isValid = false;
      break;
    }
  }
  return isValid;
};

const validateEmpty = () => {
  if (isEmpty(mediaObjectTitle.value.trim())) {
    error.value.title = t("field_empty_error_message");
  }
};

const validateCharacterLimitLocal = () => {
  if (!validateMaxCharacterLimit(mediaObjectTitle.value, characterLimit.title) && editable.value) {
    error.value.title = t("character_limit_invalid_message", {
      num: characterLimit.title,
    });
  }
  if (
    !validateMaxCharacterLimit(mediaObjectDescription.value, characterLimit.description) &&
    editable.value
  ) {
    error.value.description = t("character_limit_invalid_message", {
      num: characterLimit.description,
    });
  }
  if (
    !validateMaxCharacterLimit(mediaObjectMetadata.value.keywords, characterLimit.keywords) &&
    editable.value
  ) {
    error.value.keyword = t("keywords_total_character_limit_invalid_message", {
      num: characterLimit.keywords,
    });
  }
};

const handleTranscriptEdited = (data: { index: number; value: string }) => {
  setDirty(dirtyId.value);
  const cue = trackCues.value![data.index] as VTTCue;
  cue.text = data.value;
  isTranscriptEdited.value = true;
};

const handleTrackLoaded = (cues: TextTrackCueList) => {
  trackCues.value = cues;
  isTranscriptEdited.value = false;
};

const handleSaveTranscript = () => {
  if (!mediatrackRef.value?.validate() || !canEditTranscript.value) {
    handleError(false);
    return;
  }
  if (!isTranscriptEdited.value) return;

  return generateVttFile().then((fileResponse: { uuid: string; name: string }) => {
    const requestData = {
      uuid: uuid.value,
      id: mediaObject.value?.id,
      fileVersion: mediaObject.value?.fileVersion,
      fileEntry: {
        fileUuid: fileResponse.uuid,
        filename: fileResponse.name,
      },
    };

    return MediaObjectApi.saveMediaObjectTranscript(requestData).then((response) => {
      if (response.code === "ok") {
        transcriptionFileVersion.value = response.data.fileVersion;
        captionSrc.value = response.data.transcriptFileUrl;
        return response;
      } else {
        throw new Error("error");
      }
    });
  });
};

const generateVttFile = (): Promise<{ uuid: string; name: string }> => {
  let vttString = "WEBVTT\n\n";
  const cues = trackCues.value!;
  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i] as VTTCue;
    const startTimeVal = MediaUtils.formatToVttTimestamps(Math.ceil(cue.startTime * 1000) / 1000);
    const endTimeVal = MediaUtils.formatToVttTimestamps(Math.ceil(cue.endTime * 1000) / 1000);
    const text = cue.text.replaceAll("\n", " ");
    vttString += `${i}\n`;
    vttString += `${startTimeVal} --> ${endTimeVal}\n${text}\n\n`;
  }
  const vttBlob = new Blob([vttString], { type: "text/vtt" });
  const vttFile = new File([vttBlob], `transcript.vtt`, { type: "text/vtt" });
  return handleUploadVttFile(vttFile);
};

const handleUploadVttFile = (vttFile: File): Promise<{ uuid: string; name: string }> => {
  return new Promise((resolve) => {
    const fileEntry = { file: vttFile };
    modal.show(
      FileUploaderHidden,
      {
        uploadFlow: UploadFlow.VTT,
        fileEntry,
      },
      {
        "update:fileEntry": (uploadedResult: { uuid: string; name: string }) => {
          resolve(uploadedResult);
        },
      },
    );
  });
};

const handleSave = async () => {
  if (isProcessing.value) {
    return;
  }
  setProcessing(processingId.value);

  if (isViewingIntellectualPropertyDetail.value) {
    const isCharacterLimitValid = useMediaObject_validateCharacterLimit(ipFormData.value);
    const isEndDateValid = useMediaObject_validateEndDate(ipFormData.value);

    if (isCharacterLimitValid && isEndDateValid) {
      useMediaObject_saveMediaObjectIPDetails(getIpRequestData())
        .then((response) => {
          if (response.code === "ok") {
            isProcessing.value = false;
            removeDirty(id.value);
            handleSaveSuccess();
            hasErrorInPage.value = false;
            hasSaveIPErrors.value = false;
            mediaObject.value!.modifiedByName = response.data.modifiedByName;
            mediaObject.value!.modifiedDate = response.data.modifiedDate;
          }
        })
        .catch((err) => {
          console.error("Failed to save media object:", err);
        });
    } else {
      hasSaveIPErrors.value = true;
      handleSaveError();
      handleError(false);
    }
  } else {
    if (isViewingTranscript.value) {
      saveTranscript();
      handleSaveSuccess();
      return;
    }

    if (!validateInput()) {
      handleSaveError();
      handleError(false);
      return;
    }

    // keyword in view mode won't trigger execution to save
    // trigger save directly if only update cuepoints
    if (playable.value && !isSettingsDirty.value) {
      // skip the keyword-triggered save below, otherwise the success toast shows twice
      if (await save()) handleSaveSuccess();
      return;
    }

    handleSaveSuccess();
    execution.value = {};
    task.value = "save"; // to get latest keywords
  }
};

const handleSaveSuccess = () => {
  resetProcessing();
  dataSaveButton.value.completed = true;
};

const handleSaveError = () => {
  resetProcessing();
  dataSaveButton.value.error = true;
};

const showSavedToast = () => {
  toast.show("success", {
    title: t("media_object_saved_successfully"),
    hasTimeout: true,
  });
};

const applySavedMediaObject = (data: MediaObjectManageModel) => {
  const mo = mediaObject.value!;
  mo.status = moStatus.value === "UNPUBLISHED" ? "DRAFT" : mo.status;
  mo.modifiedByName = data.modifiedByName;
  mo.modifiedDate = data.modifiedDate;
  mo.title = mediaObjectMetadata.value.title ?? "";
  updateMediaSettings();
  MediaEventBus.$emit("updateMediaObject", mediaObject.value!);
};

// resolves to true only when the media object settings were actually saved
const save = async (): Promise<boolean> => {
  if (!isDirty(id.value) && !dirty.value) {
    showSavedToast();
    return true;
  }

  removeDirty(id.value);
  dataSaveButton.value.error = false;
  isProcessing.value = true;

  try {
    const lock = await LockApi.checkLockOwner(LOCKABLE_RESOURCE_TYPE.MEDIAOBJECT, uuid.value);
    if (lock.isLocked) return false;

    if (playable.value && !isSettingsDirty.value) {
      // only update cue points if settings is not dirty
      updateMediaSettings();
      showSavedToast();
      return true;
    }

    if (isFromMedia.value) {
      const resp = await MediaObjectApi.editMediaObjectDetails(getRequestData());
      if (resp.code !== "ok") {
        handleErrorWithToast();
        return false;
      }
      showSavedToast();
      applySavedMediaObject(resp.data);
      return true;
    }

    const resp: ApiResponse<MediaObjectManageModel> =
      await MediaObjectApi.saveMediaObject(getRequestData());
    if (resp.code !== "ok") {
      handleError(true);
      return false;
    }
    showSavedToast();
    applySavedMediaObject(resp.data);
    return true;
  } catch (err) {
    handleError(err);
    return false;
  } finally {
    // keep the error state visible after a failed save
    if (!dataSaveButton.value.error) dataSaveButton.value.completed = true;
    task.value = "";
    execution.value = {};
  }
};

const saveTranscript = () => {
  if (!isDirty(dirtyId.value)) {
    return;
  }
  if (isTranscriptEdited.value) {
    isProcessing.value = true;
    const saveTranscriptPromise = handleSaveTranscript();
    if (saveTranscriptPromise) {
      saveTranscriptPromise.then(() => {
        updateTranscriptSettings(); // to reload transcript in rich text editor
        mediaRef.value?.handleTranscriptChange(captionSrc.value); // reload transcript in media settings
        removeDirty(dirtyId.value);
        removeDirty("0");
        dataSaveButton.value.completed = true;
      });
    }
  }
};

const updateMediaSettings = () => {
  if (mediaType.value === MEDIA_TYPE.VIDEO || mediaType.value === MEDIA_TYPE.AUDIO) {
    MediaEventBus.$emit("settingsUpdated", {
      cuePoints: {
        start: MediaUtils.deformatTime(startTime.value),
        end: MediaUtils.deformatTime(endTime.value),
      },
      strictMode: strictModeLocal.value,
      updateCallback: props.params.editorSaveCallback,
      isTranscriptEnabled: isTranscriptEnabledLocal.value,
      captionSrc: captionSrc.value,
    });
    lastCueStart.value = startTime.value;
    lastCueEnd.value = endTime.value;
    lastStrictModeLocal.value = strictModeLocal.value;
    lastTranscriptEnabledLocal.value = isTranscriptEnabledLocal.value;
  }
  dirty.value = false;
  isProcessing.value = false;
};

const updateTranscriptSettings = () => {
  if (mediaType.value === MEDIA_TYPE.VIDEO || mediaType.value === MEDIA_TYPE.AUDIO) {
    MediaEventBus.$emit("settingsUpdated", {
      isTranscriptEdited: isTranscriptEdited.value,
      transcriptionFileVersion: transcriptionFileVersion.value,
      captionSrc: captionSrc.value,
    });
  }
  isProcessing.value = false;
};

const getRequestData = () => {
  const requestData = {
    uuid: uuid.value,
    title: mediaObjectMetadata.value.title,
    keywords: mediaObjectMetadata.value.keywords,
    finalize: "true",
    uploadFrom: props.params.resourceType,
    mediaObjectCopyRightModel: {
      downloadableRight: mediaObjectMetadata.value.downloadableRight,
      printableRight: mediaObjectMetadata.value.printableRight,
    },
    description: richTextDescription.value,
  };
  return requestData;
};

const fetchMediaObjectForEdit = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    MediaObjectApi.editMediaObject(uuid.value).then(
      (response) => {
        if (response.code === "error") {
          const errorMap = response.errorMap;
          if (errorMap?.locked_owner) {
            showLockedModal(errorMap.locked_owner);
            actionHidden();
          }
          handleError(errorMap);
          return;
        }

        mediaObject.value = {
          ...response.data.formData,
          isDefaultDownloadable: response.data.defaultDownloadable,
          readonly:
            hasErrorInPage.value || isEditClick.value || props.params.isFromUploadMedia
              ? false
              : isFromMedia.value
                ? true
                : response.data.readOnly,
        };

        const mo = mediaObject.value;
        mo.markable = props.params.isHTML5Interactive ? mo.markable : false;
        if (mo.markable) {
          moMarkableSelectedValues.value.push("checked-markable");
        }

        canEditTranscript.value = isMOEResource.value
          ? isSameOwnerGroup.value
          : (mo.canEditTranscript ?? false);
        mediaObjectMetadata.value.title = mo.title ?? "";

        if (isTextToSpeech.value) {
          mediaObjectMetadata.value.description = mo.description ?? "";
        } else {
          mediaObjectMetadata.value.description = MediaUtils.getTextAreaDescription(mo.description);
        }

        const copyRightModel = mo.mediaObjectCopyRightModel;
        mediaObjectMetadata.value.downloadableRight = copyRightModel?.downloadableRight as string;
        mediaObjectMetadata.value.printableRight = copyRightModel?.printableRight as string;
        mediaObjectMetadata.value.keywords = mo.keywords ?? "";

        if (licenseType.value === LICENSE_TYPE.TERM.value) {
          calculateDuration(
            copyrightData.value?.startDate as string,
            copyrightData.value?.endDate as string,
          );
        }

        MediaObjectApi.getTranscriptJob(uuid.value, {
          params: { fileVersion: transcriptionFileVersion.value },
        }).then((res) => {
          if (res.code === "error") return;
          if (!isEmpty(res.data)) {
            initTranscription(res.data);
          }
          isLoading.value = false;
          resolve();
        });
      },
      (err) => {
        reject();
        passiveApiFailHandler(err);
      },
    );
  });
};

const calculateDuration = (start: string, end: string) => {
  if (!start || !end) {
    licenseDuration.value = { years: 0, months: 0, days: 0 };
    return;
  }
  const formartStartDate = DateTimeUtils.parseLocalDateTime(start);
  const formartEndDate = DateTimeUtils.parseLocalDateTime(end);

  // Get the year of this year
  let thisFullYear = formartEndDate.getFullYear();
  // Check how many days there where in last month
  const daysInLastMonth = new Date(thisFullYear, formartEndDate.getMonth(), 0).getDate();
  // Get this month
  let thisMonth = formartEndDate.getMonth();
  // Subtract to get difference between years
  thisFullYear = thisFullYear - formartStartDate.getFullYear();
  // Subtract to get difference between months
  thisMonth = thisMonth - formartStartDate.getMonth();
  // If month is less than 0 it means that we are some months before the start date in the year.
  // So we subtract one year, and add the negative number (month) to 12. (12 + -1 = 11)
  const thisDay = formartEndDate.getDate();
  thisMonth = thisMonth - 1;
  if (thisMonth < 0) {
    thisFullYear = thisFullYear - 1;
    thisMonth = 12 + thisMonth;
    // Get ends day of the month
  }
  // Subtract the start date from the number of days in the last month, add the result to todays day of the month
  // From 1-Aug-2016 to 1-Sep-2016 is to be considered as 1 month and 1 day
  let subAddDays = daysInLastMonth - formartStartDate.getDate() + 1;
  subAddDays = thisDay + subAddDays;

  if (subAddDays >= daysInLastMonth) {
    subAddDays = subAddDays - daysInLastMonth;
    thisMonth++;
    if (thisMonth > 11) {
      thisFullYear++;
      thisMonth = 0;
    }
  }

  licenseDuration.value = {
    years: thisFullYear,
    months: thisMonth,
    days: subAddDays,
  };
};

const getHeaderLabel = (type: string) => {
  switch (type) {
    case MEDIA_TYPE.HTML5:
      return t("media_settings_interactable_settings");
    case MEDIA_TYPE.IMAGE:
      return t("media_settings_image_settings");
    case MEDIA_TYPE.VIDEO:
      return t("media_settings_video_settings");
    case MEDIA_TYPE.AUDIO:
      return t("media_settings_audio_settings");
    case MEDIA_TYPE.POWERPOINT:
    case MEDIA_TYPE.WORD:
    case MEDIA_TYPE.EXCEL:
    case MEDIA_TYPE.PDF:
      return t("media_settings_document_settings");
    case MEDIA_TYPE.ZIP:
    case MEDIA_TYPE.OTHERS:
      return t("media_settings_others_settings");
    default:
      return "-";
  }
};

const handleKeywordsInput = (tags: string) => {
  setDirty(id.value);
  isSettingsDirty.value = true;
  mediaObjectMetadata.value.keywords = tags;
};

const handleKeywordsRemove = (tags: string) => {
  setDirty(id.value);
  isSettingsDirty.value = true;
  mediaObjectMetadata.value.keywords = tags;
};

const handleExecution = (data: Record<string, unknown>) => {
  execution.value = data;
};

const handlePopulateKeywordsToSave = (tags: string) => {
  mediaObjectMetadata.value.keywords = tags;
};

const captureStart = (e: Event) => {
  e.preventDefault();
  setDirty(id.value);
  const timePromise = mediaRef.value!.getCurrentTime();
  timePromise.then((time: number) => {
    const validationError: Record<string, string> = {};
    const endInput = endTimeRef.value?.value;
    const currentTime = MediaUtils.formatTime(time);
    MediaUtils.timeSettingsValidate(validationError, currentTime, endInput, duration.value);
    if (!isEmpty(validationError.message)) {
      error.value.cueStartAndEnd = validationError.message;
    } else {
      clearCuePointsError();
      lastValidStartTime.value = currentTime;
    }
    startTimeTextInput.value = currentTime;
    cueStart.value = currentTime;
  });
};

const captureEnd = (e: Event) => {
  e.preventDefault();
  setDirty(id.value);
  const timePromise = mediaRef.value!.getCurrentTime();
  timePromise.then((time: number) => {
    const validationError: Record<string, string> = {};
    const startInput = startTimeRef.value?.value;
    const currentTime = MediaUtils.formatTime(time);
    MediaUtils.timeSettingsValidate(validationError, startInput, currentTime, duration.value);
    if (!isEmpty(validationError.message)) {
      error.value.cueStartAndEnd = validationError.message;
    } else {
      clearCuePointsError();
      lastValidEndTime.value = currentTime;
    }
    endTimeTextInput.value = currentTime;
    cueEnd.value = currentTime;
  });
};

const handleStartTime = (e) => {
  e.preventDefault();
  setDirty(id.value);
  const value = (e.target as HTMLInputElement).value;
  const endInput = endTimeRef.value?.value;
  const validationError: Record<string, string> = {};
  MediaUtils.timeSettingsValidate(validationError, value, endInput, duration.value);
  if (!isEmpty(validationError.message)) {
    error.value.cueStartAndEnd = validationError.message;
    if (validationError.cueStartError) {
      cueStart.value = lastValidStartTime.value;
      startTimeTextInput.value = value;
      return;
    }
  } else {
    mediaRef.value!.updateStartTime(MediaUtils.deformatTime(value));
    clearCuePointsError();
    lastValidStartTime.value = value;
  }
  startTimeTextInput.value = value;
  cueStart.value = value;
};

const handleEndTime = (e) => {
  e.preventDefault();
  setDirty(id.value);
  const value = (e.target as HTMLInputElement).value;
  const startInput = startTimeRef.value?.value;
  const validationError: Record<string, string> = {};
  MediaUtils.timeSettingsValidate(validationError, startInput, value, duration.value);
  if (!isEmpty(validationError.message)) {
    error.value.cueStartAndEnd = validationError.message;
    if (validationError.cueEndError) {
      cueEnd.value = lastValidEndTime.value;
      endTimeTextInput.value = value;
      return;
    }
  } else {
    clearCuePointsError();
    lastValidEndTime.value = value;
  }
  endTimeTextInput.value = value;
  cueEnd.value = value;
};

const clearCuePointsError = () => {
  error.value.cueStartAndEnd = "";
};

const handleMediaRef = (ref: InstanceType<typeof MediaComponent>) => {
  mediaRef.value = ref ?? null;
};

const handleMediaLoaded = () => {
  if (playable.value) {
    if (hasTranscript.value && props.params.isTranscriptEnabled) {
      mediaRef.value?.handleTranscriptChange(captionSrc.value);
    }
  }
};

const handleMediaReady = () => {
  isMediaReady.value = true;
};

const handleMediaPlayable = () => {
  if (playable.value) {
    const durationPromise = mediaRef.value?.getDuration();
    durationPromise?.then((dur: number) => {
      duration.value = dur;
      if (cueEnd.value === MediaUtils.formatTime(0)) {
        cueEnd.value = MediaUtils.formatTime(duration.value);
      }
      startTimeTextInput.value = startTime.value;
      lastValidStartTime.value = startTime.value;
      endTimeTextInput.value = endTime.value;
      lastValidEndTime.value = endTime.value;
    });
  }
};

const executeTask = (executionData: Record<string, unknown>) => {
  if (task.value === "save") {
    handlePopulateKeywordsToSave(executionData.data as string);
    save();
    task.value = ""; // reset task, to allow keywords' message watcher to re-trigger
  }
};

const stopTaskExecution = (executionData: Record<string, unknown>) => {
  emit("failed", { error: executionData.errors });
  task.value = "";
  execution.value = {};
  handleError(false);
};

const handleErrorWithToast = () => {
  isProcessing.value = false;
  resetProcessing();
  passiveApiFailHandler();
};

const handleError = (isApiError: unknown) => {
  isProcessing.value = false;
  dataSaveButton.value.error = true;
  if (!isApiError) {
    dataSaveButton.value.errorMessage = "";
  }
  resetProcessing();
  dataSaveButton.value.error = true;
};

const showViewEditTranscriptSubpage = () => {
  promptPageDirty().then((response) => {
    if (response) {
      error.value = {
        ...error.value,
        cueStartAndEnd: "",
        title: "",
        description: "",
      };
      isViewingTranscript.value = true;
      hasParentSubpage.value = true;
      titles.value.push(t("media_settings_transcript_page_title"));
    }
  });
};

const handleGenerateTranscript = () => {
  if (hasTranscript.value) {
    const props = {
      title: t("media_settings_regenerate_transcript_prompt_title"),
      content: t("media_settings_regenerate_transcript_prompt_content"),
      primaryBtnLabel: t("button_confirm"),
      secondaryBtnLabel: t("button_cancel"),
      actionPrimary: () => {
        generateTranscript();
      },
      actionHidden: () => {
        // empty
      },
    };
    modal.showMessage("warning", props);
  } else {
    generateTranscript();
  }
};

const generateTranscript = () => {
  generateTranscriptButton.value.loading = true;
  MediaObjectApi.transcribeMediaObject(uuid.value, {
    lessonUuid: props.params.context?.lessonUuid as string,
    activityId: MediaUtils.resolveTranscribeActivityId(props.params.context?.pageId),
  }).then((response) => {
    if (response.code === "ok") {
      setDirty(id.value);
      isGeneratingTranscript.value = true;
      hasGeneratedTranscript.value = false;
      transcriptionFileVersion.value = response.data.fileVersion;
      if (isTranscriptEnabledLocal.value) {
        // reset transcript enabled flag
        handleTranscriptEnabledChange(false);
      }
      getTranscriptStatus().then();
    } else {
      generateTranscriptButton.value.error = true;
    }
  }, passiveApiFailHandler);
};

const initTranscription = (jobData: TranscriptionJobModel) => {
  hasGeneratedTranscript.value = false;
  if (!jobData) return;

  const status = jobData.status;
  if (status === TRANSCRIPTION_STATUS.IN_PROGRESS) {
    isGeneratingTranscript.value = true;
    generateTranscriptButton.value.loading = true;
    getTranscriptStatus();
  } else if (status === TRANSCRIPTION_STATUS.FAILED) {
    showTranscriptError.value = true;
  }

  captionSrc.value = !isEmpty(jobData.transcriptFileUrl) ? jobData.transcriptFileUrl : "";
  hasGeneratedTranscript.value = !isEmpty(captionSrc.value);

  if (jobData.fileVersion !== transcriptionFileVersion.value) {
    transcriptionFileVersion.value = jobData.fileVersion;
  }

  if (!hasGeneratedTranscript.value && props.params.isTranscriptEnabled) {
    // remove check if transcript not generated
    handleTranscriptEnabledChange(false, true);
  }
};

const getTranscriptStatus = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    PollingUtils.pollingWithFibonacciInterval(
      uuid.value,
      MediaObjectApi.getTranscriptStatus,
      continueCheckingTranscriptStatus,
      Date.now(),
      300000,
      2000,
      3000,
    ).then(
      (response) => {
        if (response.code !== "ok") {
          reject();
          return;
        }
        isGeneratingTranscript.value = false;
        generateTranscriptButton.value.loading = false;
        showTranscriptError.value = response.data === TRANSCRIPTION_STATUS.FAILED;
        updateCaptionSrc();
        resolve();
      },
      () => {
        reject();
      },
    );
  });
};

const continueCheckingTranscriptStatus = (response: ApiResponse<string>) => {
  if (response.code !== "ok") return false;
  return response.data && response.data === TRANSCRIPTION_STATUS.IN_PROGRESS;
};

const updateCaptionSrc = () => {
  MediaObjectApi.getTranscriptJob(uuid.value, {
    params: { fileVersion: transcriptionFileVersion.value },
  }).then((response) => {
    if (response.code === "ok") {
      captionSrc.value = response.data.transcriptFileUrl;
      mediaRef.value?.handleTranscriptChange(captionSrc.value);
      hasGeneratedTranscript.value = !isEmpty(captionSrc.value);
    }
  });
};

const handleCloseSubpage = () => {
  promptPageDirty().then((response) => {
    if (response) {
      dirty.value = false;
      removeDirty(dirtyId.value);
      if (props.params.editorSaveCallback) props.params.editorSaveCallback();
      actionHidden();
      EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
    }
  });
  if (isDefined(route.query.uuid)) {
    const ownerGroupsString = manageResourceStoreRef.value.getOwnerGroupsSelected().join(",");
    router.push({
      query: {
        keyword: "",
        location: "MOE",
        resource: "MEDIA",
        ownerGroups: ownerGroupsString,
        status: "ALL",
        resourceType: "ALL",
      },
    });
  }
};

const showLockedModal = (user: string) => {
  const resourceType = RESOURCE_TYPE.MEDIA.toLowerCase();
  const resourceTypeTitle = resourceType.charAt(0).toUpperCase() + resourceType.slice(1);

  const title = t("resource_lock_title", { resource: resourceTypeTitle });
  const content = t("resource_lock_content", {
    resource: resourceType,
    user: SecurityUtils.sanitizeText(user),
  });

  const props = {
    title,
    content,
    primaryBtnLabel: t("button_ok"),
    use_secondaryButton: false,
    actionPrimary: () => {
      // empty
    },
    actionHidden: () => {
      // empty
    },
  };
  modal.showMessage("warning", props);
};

const promptPageDirty = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!isDirty(dirtyId.value)) return resolve(true);
    return resolve(confirm(t("dirty_confirmation_prompt")));
  });
};

const setSelectedValues = () => {
  if (!isMOEResource.value) {
    // only handle non-MOE MO as MOE MO not showing allow download and print checkbox
    if (mediaObjectMetadata.value.downloadableRight === DOWNLOADABLE_RIGHT.YES.value) {
      moDownloadableSelectedValues.value.push("checked-downloadable");
    }
    if (mediaObjectMetadata.value.printableRight === PRINTABLE_RIGHT.YES.value) {
      moPrintableSelectedValues.value.push("checked-printable");
    }
  }

  if (lastTranscriptEnabledLocal.value) {
    showTranscriptSelectedValues.value.push("checked-transcript-enabled");
  }
  if (!strictModeLocal.value) {
    nonStrictModeSelectedValues.value.push("checked-non-strict-mode");
  }
};

const setRMSResourceType = () => {
  switch (mediaType.value) {
    case MEDIA_TYPE.IMAGE:
      ipFormData.value.rmsType = RMS_TYPE.IMAGE_ILLUSTRATION.value;
      break;
    case MEDIA_TYPE.VIDEO:
      ipFormData.value.rmsType = RMS_TYPE.VIDEO_ANIMATION.value;
      break;
    case MEDIA_TYPE.HTML5:
      ipFormData.value.rmsType = RMS_TYPE.INTERACTIVE.value;
      break;
    case MEDIA_TYPE.AUDIO:
      ipFormData.value.rmsType = RMS_TYPE.AUDIO_MUSIC.value;
      break;
    case MEDIA_TYPE.POWERPOINT:
    case MEDIA_TYPE.WORD:
    case MEDIA_TYPE.EXCEL:
    case MEDIA_TYPE.PDF:
      ipFormData.value.rmsType = RMS_TYPE.TEXT_DATA_WITH_IMAGE.value;
      break;
    case MEDIA_TYPE.ZIP:
    case MEDIA_TYPE.OTHERS:
      ipFormData.value.rmsType = RMS_TYPE.OTHERS.value;
      break;
    default:
      ipFormData.value.rmsType = RMS_TYPE.OTHERS.value;
  }
};

const setCuePointValue = () => {
  cueStart.value = lastCueStart.value;
  cueEnd.value = lastCueEnd.value;
};

const setRichTextValue = (value: string) => {
  // create richtext format
  const placeholder = DOMUtils.createElement("div", { class: "mo-description" });
  const splitText = RichTextUtils.splitTextByNewLine(value);
  splitText.forEach((text: string) => {
    const childParagraph = DOMUtils.createElement("div", { class: "paragraph" });
    childParagraph.innerHTML = text;
    placeholder.appendChild(childParagraph);
  });
  richTextDescription.value = placeholder.outerHTML;
};

const handleViewStatusHistory = () => {
  contentSubpageInstance.show(StatusHistoryContentSubpage, {
    baseTitle: [mediaObjectTitle.value],
    resourceUuid: mediaObject.value!.uuid,
    resourceDomain: "MOE",
    resourceStatus: mediaObject.value!.status,
    resourceType: RESOURCE_TYPE.MEDIA,
    hasParentSubpage: true,
  });
};

const handleDelete = () => {
  const props = {
    title: t("media_object_delete_confirmation_title"),
    content: t("media_object_delete_confirmation_message"),
    primaryBtnLabel: t("button_ok"),
    secondaryBtnLabel: t("button_cancel"),
    actionPrimary: () => {
      deleteMO();
    },
  };
  modal.showMessage("warning", props);
};

const deleteMO = () => {
  MediaObjectApi.deleteMediaObject(uuid.value).then((response) => {
    if (response.code === "ok") {
      toast.show("success", {
        title: t("media_object_delete_success_toast_message"),
        hasTimeout: true,
      });
      handleCloseSubpage();
    }
  }, passiveApiFailHandler);
};

const handleShowIntellectualPropertyDetail = () => {
  promptPageDirty().then((response) => {
    if (response) {
      isViewingIntellectualPropertyDetail.value = true;
      hasParentSubpage.value = true;
      parentSubpageName.value = titles.value.slice(-1)[0];
      titles.value.push(t("media_object_intellectual_property_details"));
      dataSaveButton.value.completed = false;
      dataSaveButton.value.error = false;
    }
  });
};

const handleBackFromIntellectualPropertyDetail = () => {
  fetchMediaObjectForEdit().then(() => {
    // to re-fetch mo metadata base on latest save
    moDownloadableSelectedValues.value = [];
    moPrintableSelectedValues.value = [];
    showTranscriptSelectedValues.value = [];
    strictModeLocal.value = lastStrictModeLocal.value;
    setCuePointValue();
    if (!isEmpty(mediaObjectMetadata.value.description)) {
      setRichTextValue(mediaObjectMetadata.value.description!);
    }
    removeDirty(dirtyId.value);
    hasSaveIPErrors.value = false;
    isViewingTranscript.value = false;
    isViewingIntellectualPropertyDetail.value = false;
    parentSubpageName.value = props.params.parentSubpageName ?? "";
    hasParentSubpage.value = props.params.hasParentSubpage ?? false;
    titles.value.pop(); // remove the last part
    fetchMediaObjectIPDetails();
    setSelectedValues();
    dataSaveButton.value.completed = false;
    dataSaveButton.value.error = false;
  });
};

const handleBackFromTranscript = () => {
  parentSubpageName.value = props.params.parentSubpageName ?? "";
  hasParentSubpage.value = props.params.hasParentSubpage ?? false;
  isViewingTranscript.value = false;
  titles.value.pop();
  dataSaveButton.value.completed = false;
  dataSaveButton.value.error = false;
};

const handleIpContentChange = (value) => {
  ipFormData.value = value;
  setDirty(dirtyId.value);
};

const handleEditIPDetails = () => {
  fetchMediaObjectIPDetails();
  isEditClick.value = true;
  mediaObject.value!.readonly = false;
};

const handleIpErrorStatus = (value: boolean) => {
  hasErrorInPage.value = value;
  hasSaveIPErrors.value = value;
};

const handleSubmitForApproval = () => {
  if (
    useMediaObject_validateCharacterLimit(ipFormData.value) &&
    useMediaObject_validateEmptyField(ipFormData.value) &&
    useMediaObject_validateEndDate(ipFormData.value)
  ) {
    hasErrorInPage.value = false;
    useMediaObject_showSubmitForApprovalModal(
      () => {
        useMediaObject_submitForApproval(uuid.value, true).then((response) => {
          if (response.code === "ok") {
            const mo = mediaObject.value!;
            mo.status = "PENDING_APPROVAL";
            mo.readonly = true;
            isEditClick.value = false;
            emit("updateMediaObject", mediaObject.value!);
          }
        });
      },
      () => {
        // empty
      },
    );
  } else {
    hasErrorInPage.value = true;
    mediaObject.value!.readonly = false;
    toast.show("error", {
      title: t("media_object_unable_submit_for_approval_title"),
      subTitle: t("media_object_field_validation_toast"),
    });
  }
};

const handleApprove = () => {
  if (
    useMediaObject_validateCharacterLimit(ipFormData.value) &&
    useMediaObject_validateEmptyField(ipFormData.value) &&
    useMediaObject_validateEndDate(ipFormData.value)
  ) {
    hasErrorInPage.value = false;
    useMediaObject_showApproveModal(
      () => {
        useMediaObject_approve(uuid.value, true).then((response) => {
          if (response.code === "ok") {
            const mo = mediaObject.value!;
            mo.status = "APPROVED";
            mo.readonly = true;
            isEditClick.value = false;
            emit("updateMediaObject", mediaObject.value!);
          }
        });
      },
      () => {
        // empty
      },
    );
  } else {
    hasErrorInPage.value = true;
    mediaObject.value!.readonly = false;
    toast.show("error", {
      title: t("media_object_unable_approval_title"),
      subTitle: t("media_object_field_validation_toast"),
    });
  }
};

const handleReject = () => {
  useMediaObject_showRejectModal(
    () => {
      useMediaObject_reject(uuid.value)
        .then(() => {
          mediaObject.value!.status = "REJECTED";
          emit("updateMediaObject", mediaObject.value!);
        })
        .catch((err: unknown) => {
          console.error("Failed to REJECTED:", err);
        });
    },
    () => {
      // empty
    },
  );
};

const handleUnpublish = () => {
  useMediaObject_showUnpublishModal(
    () => {
      useMediaObject_unpublish(uuid.value)
        .then(() => {
          mediaObject.value!.status = "UNPUBLISHED";
          emit("updateMediaObject", mediaObject.value!);
        })
        .catch((err: unknown) => {
          console.error("Failed to UNPUBLISHED:", err);
        });
    },
    () => {
      // empty
    },
  );
};

const fetchMediaObjectIPDetails = () => {
  MediaObjectApi.getMediaObjectIPDetails(uuid.value).then((response) => {
    if (response.code !== "ok") {
      passiveApiFailHandler();
      return;
    }
    mediaObjectIPData.value = response.data;
    // bind existing info into IP field
    for (const key in ipFormData.value) {
      if (
        mediaObjectIPData.value &&
        Object.prototype.hasOwnProperty.call(mediaObjectIPData.value, key)
      ) {
        const formData = ipFormData.value;
        formData[key] = mediaObjectIPData.value[key] != null ? mediaObjectIPData.value[key] : "";
        // format date
        if ((key === "startDate" || key === "endDate") && !isEmpty(formData[key])) {
          const date = DateTimeUtils.parseLocalDateTime(mediaObjectIPData.value[key]);
          formData[key] = DateTimeFormatter.formatToDateOnly(date);
        }
      }
    }
    if (isMOEResource.value && (isMOEModule.value || isFromMedia.value)) {
      if (ipFormData.value.rmsType === "") {
        setRMSResourceType();
      }
    }
  });
};

const handleEdit = () => {
  MediaObjectApi.getMediaObjectDetails(uuid.value).then((response) => {
    if (response.code === "ok") {
      isEditClick.value = true;
      const data = response.data;
      if (isEmpty(data)) return;
      mediaObjectMetadata.value.title = data.title as string;
      mediaObjectMetadata.value.description = MediaUtils.getTextAreaDescription(data.description);
      mediaObjectMetadata.value.keywords = data.keywords ? data.keywords : "";
      mediaObject.value!.readonly = data.readonly;
    } else {
      passiveApiFailHandler();
    }
  });
};

const handleSaveMO = () => {
  if (!validateInput()) {
    toast.show("error", {
      title: t("media_object_fail_save_toast_title"),
      subTitle: t("media_object_check_for_error_message"),
      hasTimeout: true,
    });
    return;
  }
  save();
};

const getIpRequestData = () =>
  MediaUtils.buildIpRequestData(ipFormData.value, mediaObject.value!.id as number);

const fetchLevelSubjectList = () => {
  const fetchLevelAndCoursePromise = LevelStreamApi.fetchAllLevelAndCourse();
  const fetchSubjectPromise = SubjectApi.getCategorizedSubjects();

  Promise.all([fetchLevelAndCoursePromise, fetchSubjectPromise])
    .then(([levelResponse, subjectResponse]) => {
      if (levelResponse.code === "ok") {
        levelList.value = levelResponse.data;
      }
      if (subjectResponse.code === "ok") {
        subjectList.value = subjectResponse.data;
      }
    })
    .catch(passiveApiFailHandler);
};

const initializeData = () => {
  OwnerGroupApi.fetchAllOwnerGroup().then((response) => {
    if (response.code !== "ok") {
      passiveApiFailHandler();
      return;
    }
    userOwnerGroups.value = response.data.assigned;
  });

  fetchMediaObjectForEdit().then(() => {
    setSelectedValues();
    if (!isEmpty(mediaObjectMetadata.value.description)) {
      setRichTextValue(mediaObjectMetadata.value.description!);
    }
    titles.value.push(mediaObjectMetadata.value.title!);
    titles.value.push(getHeaderLabel((mediaObject.value?.fileType as string).toUpperCase()));
  });

  if (isMOEResource.value && isCAOrCO.value) {
    fetchMediaObjectIPDetails();
    fetchLevelSubjectList();
  }
};

// Watchers
watch(execution, (newExecution) => {
  if (Object.keys(newExecution).length < 1) return;
  if ((newExecution as Record<string, unknown>).code === "error") {
    stopTaskExecution(newExecution as Record<string, unknown>);
  } else {
    executeTask(newExecution as Record<string, unknown>);
  }
});

watch(hasTranscript, (value) => {
  generateTranscriptButton.value.kind = value ? "primary" : "tertiary";
});

watch(captionSrc, (value) => {
  if (props.params.isTranscriptEnabled) {
    mediaRef.value?.handleTranscriptChange(value);
  }
});

// Provide
provide("ipContext", {
  ipFormData: ipFormData.value,
  formError: error.value,
});

// Lifecycle hooks
onBeforeUnmount(() => {
  LockApi.unlock(LOCKABLE_RESOURCE_TYPE.MEDIAOBJECT, uuid.value);
});

// Created
initializeData();
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.media-settings-content-subpage {
  &.bx--modal {
    .bx--modal-container {
      > .content {
        > .sub {
          &:has(.media-settings) {
            padding: 0;
          }
        }
      }
    }
  }

  .media-component {
    .tools {
      flex-wrap: nowrap;
    }
  }
  .bx--form {
    margin: 0;
  }
  .sub {
    .bx--text-input__field-wrapper {
      > input {
        background-color: $field-02;
      }
    }
  }

  .copyright-duration {
    .field-value {
      > span {
        &:not(:last-of-type) {
          margin-right: $spacing-06;
        }
      }
    }
  }

  .field-set {
    &.description {
      pre {
        white-space: normal;
      }
    }
  }

  .media-settings {
    padding: $spacing-05;
    position: relative;
    .field-set {
      .action,
      .time-input {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: $spacing-05;
      }

      .action {
        margin-top: $spacing-03;
        margin-bottom: $spacing-05;
      }

      &.status {
        display: flex;
        align-items: center;
        column-gap: $spacing-05;
        flex-flow: wrap;
        .status-label {
          margin-top: 0;
        }
        .status-history {
          flex: 1 0 100%;
          margin-top: 0;
          .bx--link {
            @include carbon--type-style("button-01", true);
            margin: 0;
          }
        }
        .field-label {
          @include carbon--type-style("productive-heading-01", true);
        }
      }

      &.created-by,
      &.modified-by {
        .field-value {
          display: flex;
          flex-flow: row wrap;

          .label-component {
            span {
              @include carbon--type-style("label-01", true);
              color: $text-01;
            }
          }

          .date {
            flex: 0 0 100%;
            margin-top: $spacing-03;
          }
        }
      }

      &.type-checkbox {
        .input-checkbox {
          margin: 0;
        }
      }
    }

    .bx--text-input,
    .bx--text-area {
      background-color: $ui-background;
    }
  }

  .transcript-settings {
    display: flex;
    flex-flow: column;
    height: 100%;
    > .transcript-title {
      @include carbon--type-style("productive-heading-01", true);
      margin-bottom: $spacing-03;
    }
    > .transcript-content {
      display: flex;
      justify-content: center;
      &:has(> .loading) {
        flex-grow: 1;
      }
      & ~ .action {
        display: flex;
        justify-content: center;
        padding: $spacing-05 0;
      }
    }
  }

  .view-transcript {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: $spacing-02;
    margin-bottom: $spacing-07;
    padding-bottom: $spacing-07;
    border-bottom: $divider-02;

    .bx--link {
      &.disabled {
        opacity: 1;
        color: $disabled-02;
      }
    }

    .bx--tooltip__trigger {
      margin-left: $spacing-02;

      .sls-icon {
        [stroke] {
          stroke: $icon-02;
        }

        [fill] {
          fill: $icon-02;
        }
      }
    }
  }

  .sub-content-action {
    position: sticky;
    bottom: 0;
    background-color: $ui-01;
    margin: 0 $spacing-05;
    padding: $spacing-05 0;
    border-top: $divider-02;
    z-index: 300;

    .bx--inline-notification {
      margin: 0 0 $spacing-05 0;
    }
  }
  .button-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 $spacing-05;
  }
}
</style>
